<template>
    <section class="hr-tile">
        <ul class="breadcrumbs">
            <li class="breadcrumb-item">
                <router-link :to="'/'">Home</router-link>
            </li>
            <li v-if="!currentArticle" class="breadcrumb-item">
                <span>News</span>
            </li>
            <li v-else class="breadcrumb-item">
                <router-link :to="'/news'">News</router-link>
            </li>
            <li v-if="currentArticle" class="breadcrumb-item">
                <span>{{ currentArticle.Title }}</span>
            </li>
        </ul>
    </section>
    <div class="columns">
        <div class="column is-two-thirds">
            <section v-if="!currentArticle" class="hr-tile greeble-tr">
                <h4 class="title is-4">
                    Latest Articles
                </h4>
                <NewsFeed :thumb=true :gameNames="gameNames" :newsFeed="filteredNewsFeed.slice((page-1)*10, page*10)"></NewsFeed>
                <div v-if="filteredNewsFeed.length > 10" class="news-pagination">
                    <button class="button is-primary" @click="page--" :disabled="page === 1">Previous</button>
                    <span>Page {{ page }} of {{ Math.ceil(filteredNewsFeed.length / 10) }}</span>
                    <button class="button is-primary" @click="page++" :disabled="page === Math.ceil(filteredNewsFeed.length / 10)">Next</button>
                </div>
            </section>
            <section v-else class="hr-tile greeble-tr">
                <h4 class="title is-3">
                    {{ currentArticle.Title }}
                </h4>
                <NewsArticleComponent :gameNames="gameNames" :article="currentArticle"></NewsArticleComponent>
            </section>
        </div>
        <div class="column is-one-third">
            <section v-if="hasAdminClaim" class="hr-tile admin-panel">
                <h4 class="title is-4">
                    Admin Options
                </h4>
                <div class="admin-options">
                    <router-link class="button is-primary" :to="'/admin/news/create'">New Article</router-link>
                    <router-link class="button is-secondary" :to='"/admin/news"'>Manage Articles</router-link>
                    <router-link v-if="currentArticle" class="button is-secondary" :to="'/admin/news/' + currentArticle.UrlStub">Edit Article</router-link>
                </div>
            </section>
            <section class="hr-tile" v-if="allNews">
                <h4 class="title is-4">
                    Featured Articles
                </h4>
                <NewsFeed :thumb=false :gameNames="gameNames" :newsFeed="allNews.filter(i => i.Featured)"></NewsFeed>
            </section>
            <section v-if="!currentArticle" class="hr-tile">
                <h4 class="title is-4">
                    Filter Articles
                </h4>
                <div class="news-options">
                    <input type="text" class="input" style="flex-grow:1" placeholder="Search" v-model="search" @keyup="applySearch()" />
                    <label>Filter News for Specific Games</label>
                    <div class="game-selector">
                        <div class="game-options" v-for="game in games">
                            <input type="checkbox" v-bind:checked="filteredGames.get(game.Id)" @change="filterGame(game.Id)"/>
                            <span>{{ game.Name }}</span>
                        </div>
                    </div>
                </div>
            </section>
        </div>
    </div>
</template>

<script lang="ts">
import { Component, Vue, Watch } from "vue-facing-decorator";
import NewsFeed from "@/components/News/NewsFeed.vue";
import { Game, NewsArticle } from "@/api/";
import { GameStore } from "@/store/games";
import AuthStore from "@/store/auth";
import { NewsService } from "@/api/services/NewsService";
import NewsArticleComponent from "@/components/News/NewsArticleComponent.vue";

@Component({
  components: {
    NewsFeed,
    NewsArticleComponent
  }
})
export default class News extends Vue {
    allNews: NewsArticle[] | undefined = undefined;

    filteredNewsFeed: NewsArticle[] | undefined = [];

    page: number = 1;

    games: Game[] = new Array();
    gameNames: Map<string | undefined, string | undefined> = new Map<string, string>();

    search: string | null = null;
    filteredGames: Map<string, boolean> = new Map();
    
    currentArticle: NewsArticle | null = null;

    @Watch("$route.params.newsStub")
    async onRouteChange() {
        await this.load();
    }

    async load() {
        let stub = <string>this.$route.params.newsStub

        await AuthStore.pendingAuth;
        this.allNews = await NewsService.getNews();

        this.filteredNewsFeed = this.allNews;

        if (stub == null) {
            this.currentArticle = null;
            return;
        }

        this.currentArticle = await NewsService.getNewsByStub(stub);
    }

    async created() {
        this.games = (await GameStore.get()).games.filter((g) => !g.Hidden);
        this.gameNames = new Map(this.games.map(g => [g.Id, g.Name]));

        await this.load();
    }

    get hasAdminClaim() {
        return AuthStore.hasAdminClaim();
    }

    filterGame(gameId: string) {
        if (this.filteredGames.get(gameId)) {
            this.filteredGames.delete(gameId);
        } else {
            this.filteredGames.set(gameId, true);
        }
        this.applySearch();
    }

    applySearch() {
        this.page = 1;

        if (this.allNews == null) return;

        this.filteredNewsFeed = this.allNews.filter(item => 
            (item.Title?.toLowerCase().includes((this.search ?? "").toLowerCase()) ?? false) &&
            (item.Games?.some(g => this.filteredGames.get(g)) || !this.filteredGames.size)
        );
    }
}
</script>

<style lang="scss" scoped>
@use "@/assets/haloruns_vars.scss" as *;

.admin-panel {
    display: flex;
    flex-direction: column;
    gap: 1rem;
    h4 {
        margin-bottom: 0;
    }
}

.admin-options {
    display: flex;
    gap: 1rem;
}

.post-options {
    display: flex;
    flex-direction: row;
    gap: 0.5rem;
}

.featured-toggle {
    display: flex;
    align-items: center;
    gap: 0.5rem;
}

.news-options {
    display: flex;
    flex-direction: column;
    gap: 1rem;
    margin-bottom: 1.5rem;
}

.news-pagination {
    display: flex;
    justify-content: center;
    align-items: center;
    gap: 1rem;
}

.game-selector {
    background: black;
    margin: 0;
    border: none;
    overflow-y: auto;
    flex-grow: 1;
}

.game-options {
    margin: 10px;

    input[type=checkbox] {
        margin-right: 10px;

    }

    input[type="checkbox"]::before {
        content: "";
        width: 0.65em;
        height: 0.65em;
        transform: scale(0);
        transition: 120ms transform ease-in-out;
        box-shadow: inset 1em 1em var(--form-control-color);
    }

    input[type="checkbox"]:checked::before {
        transform: scale(1);
    }
}

</style>